import React, { useEffect, useMemo, useState } from "react";
import { performanceTaskAPI } from "../services/api";
import "./PerformanceTasksPage.css";

const normalizeTaskRouteId = (value) => String(value || "").trim().toLowerCase();

const buildUnitLabel = (task) => {
  if (Number.isFinite(Number(task?.unitNumber))) {
    return `Unit ${task.unitNumber}`;
  }
  if (task?.unitTitle) return task.unitTitle;
  return "Task";
};

const isPdfResource = (resource) => {
  const type = String(resource?.resource_type || "").toLowerCase();
  const mime = String(resource?.mime_type || "").toLowerCase();
  const filePath = String(resource?.file_path || "").toLowerCase();
  const url = String(resource?.url || "").toLowerCase();
  return (
    type === "pdf"
    || mime.includes("pdf")
    || filePath.endsWith(".pdf")
    || url.endsWith(".pdf")
  );
};

const isDocxResource = (resource) => {
  const type = String(resource?.resource_type || "").toLowerCase();
  const mime = String(resource?.mime_type || "").toLowerCase();
  const filePath = String(resource?.file_path || "").toLowerCase();
  const url = String(resource?.url || "").toLowerCase();
  return (
    type === "docx"
    || mime.includes("wordprocessingml")
    || filePath.endsWith(".docx")
    || url.endsWith(".docx")
  );
};

const getResourceHref = (resource) => {
  if (!resource) return "";
  return String(resource.file_path || resource.url || "").trim();
};

const PerformanceTasksPage = ({ taskId, courseId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [docxText, setDocxText] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadTasks = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await performanceTaskAPI.list(courseId || undefined);
        if (cancelled) return;
        setTasks(Array.isArray(response.data) ? response.data : []);
      } catch (loadError) {
        if (cancelled) return;
        console.error("Error loading performance tasks:", loadError);
        setTasks([]);
        setError("Unable to load performance tasks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadTasks();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const selectedTask = useMemo(() => {
    if (!tasks.length) return null;
    const normalized = normalizeTaskRouteId(taskId);
    if (!normalized) return tasks[0];

    return (
      tasks.find((task) => normalizeTaskRouteId(task.taskKey) === normalized)
      || tasks.find((task) => normalizeTaskRouteId(task.id) === normalized)
      || tasks[0]
    );
  }, [taskId, tasks]);

  const selectedResource = useMemo(() => {
    const resources = Array.isArray(selectedTask?.resources) ? selectedTask.resources : [];
    return resources.find(isPdfResource)
      || resources.find(isDocxResource)
      || resources[0]
      || null;
  }, [selectedTask]);

  useEffect(() => {
    if (!selectedResource || !isDocxResource(selectedResource)) {
      setDocxText("");
      return;
    }

    const textPath = String(selectedResource.text_path || "").trim();
    if (!textPath) {
      setDocxText("");
      return;
    }

    let cancelled = false;
    fetch(textPath)
      .then((response) => (response.ok ? response.text() : ""))
      .then((text) => {
        if (!cancelled) setDocxText(String(text || ""));
      })
      .catch(() => {
        if (!cancelled) setDocxText("");
      });

    return () => {
      cancelled = true;
    };
  }, [selectedResource]);

  const goToTask = (task) => {
    if (!task?.taskKey) return;
    window.location.hash = `/performance-tasks/${task.taskKey}`;
  };

  return (
    <div className="panel-card performance-tasks-page">
      <div className="performance-tasks-header">
        <h2>Performance Tasks</h2>
        <p>Standards-aligned tasks with resources, rubric criteria, and checkpoints.</p>
      </div>

      {loading ? <p>Loading performance tasks...</p> : null}
      {error ? <p>{error}</p> : null}
      {!loading && !error && tasks.length === 0 ? (
        <p>No performance tasks found for this course yet.</p>
      ) : null}

      {tasks.length > 0 ? (
        <div className="performance-task-tabs">
          {tasks.map((task) => {
            const isActive = selectedTask?.id === task.id;
            return (
              <button
                key={task.id || task.taskKey}
                type="button"
                className={`nav-lite-btn ${isActive ? "active-task" : ""}`}
                onClick={() => goToTask(task)}
              >
                {buildUnitLabel(task)}: {task.title}
              </button>
            );
          })}
        </div>
      ) : null}

      {selectedTask ? (
        <section className="performance-task-viewer">
          <header className="task-meta">
            <h3>
              {buildUnitLabel(selectedTask)}: {selectedTask.title}
            </h3>
            {selectedTask.courseName ? <p>{selectedTask.courseName}</p> : null}
            {selectedTask.summary ? <p>{selectedTask.summary}</p> : null}
            {selectedTask.instructions ? <p>{selectedTask.instructions}</p> : null}
          </header>

          {Array.isArray(selectedTask.standards) && selectedTask.standards.length > 0 ? (
            <div className="task-standards">
              <h4>Aligned Standards</h4>
              <ul>
                {selectedTask.standards.map((standard) => (
                  <li key={`${standard.teks_id}-${standard.teks_code}`}>
                    <strong>{standard.teks_code || "TEKS"}</strong>
                    {standard.alignment_role ? ` (${standard.alignment_role})` : ""}
                    {standard.description ? ` - ${standard.description}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {Array.isArray(selectedTask.checkpoints) && selectedTask.checkpoints.length > 0 ? (
            <div className="task-checkpoints">
              <h4>Checkpoints</h4>
              <ul>
                {selectedTask.checkpoints.map((checkpoint) => (
                  <li key={`${checkpoint.id || checkpoint.checkpoint_order}-${checkpoint.label}`}>
                    <strong>{checkpoint.label}</strong>
                    {Number.isFinite(Number(checkpoint.due_offset_days))
                      ? ` (day +${checkpoint.due_offset_days})`
                      : ""}
                    {checkpoint.notes ? ` - ${checkpoint.notes}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {Array.isArray(selectedTask.rubricCriteria) && selectedTask.rubricCriteria.length > 0 ? (
            <div className="task-rubric">
              <h4>Rubric Criteria</h4>
              <ul>
                {selectedTask.rubricCriteria.map((criterion) => (
                  <li
                    key={`${criterion.id || criterion.criterion_order}-${criterion.criterion_label}`}
                  >
                    <strong>{criterion.criterion_label}</strong>
                    {criterion.max_points ? ` (${criterion.max_points} pts)` : ""}
                    {criterion.descriptor ? ` - ${criterion.descriptor}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {selectedResource && isPdfResource(selectedResource) ? (
            <>
              <iframe
                title={`${selectedTask.title} Performance Task`}
                src={getResourceHref(selectedResource)}
                className="task-frame"
              />
              <a
                className="primary-btn task-download"
                href={getResourceHref(selectedResource)}
                target="_blank"
                rel="noreferrer"
              >
                Open in New Tab
              </a>
            </>
          ) : null}

          {selectedResource && isDocxResource(selectedResource) ? (
            <>
              <div className="docx-preview">
                <pre>{docxText || "Loading task content..."}</pre>
              </div>
              <a
                className="primary-btn task-download"
                href={getResourceHref(selectedResource)}
                target="_blank"
                rel="noreferrer"
              >
                Download DOCX
              </a>
            </>
          ) : null}

          {selectedResource && !isPdfResource(selectedResource) && !isDocxResource(selectedResource) ? (
            <a
              className="primary-btn task-download"
              href={getResourceHref(selectedResource)}
              target="_blank"
              rel="noreferrer"
            >
              Open Task Resource
            </a>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default PerformanceTasksPage;
